/**
 * Itens de navegação do app do colaborador — fonte ÚNICA usada pela `BottomBar`
 * (barra inferior + sheet do Menu, celular/tablet) e pelo `MenuLateral` (desktop).
 *
 * WEB: extraído de `components/navigation/BottomBar.tsx` do app nativo SEM mudar
 * nenhuma regra: mesmas flags do /iniciar, mesmos grupos, mesmos destinos, mesmos
 * ícones, mesmo badge de documentos e o MESMO fluxo de "Sair" (bloqueio por pendência
 * de sincronização + limpeza que preserva o que ainda não subiu). O que muda é só
 * quem desenha: os dois menus consomem daqui para nunca divergirem.
 */
import {
  permiteEnviarAtestado,
  permiteSolicitarAbono,
} from "@/components/solicitacao/comum";
import { useAuth } from "@/contexts/AuthContext";
import {
  mostraEspelhoNoMenu,
  permiteRegistrarPonto,
  registraPonto,
} from "@/utils/permissoes";
import {
  carregarSnapshotLocalDocs,
  resolverLista,
  sessaoDocsConfirmada,
  type DocSnapshot,
} from "@/services/documentoEstado";
import { pontoAbertoHoje } from "@/services/PontoJornada";
import { checklistsLivres } from "@/utils/checklist";
import { podeUsarTrocaPlantao } from "@/utils/trocaPlantao";
import { savePortalChoice } from "@/utils/appEnvironmentStorage";
import { SyncService } from "@/services/Sync";
import { AlertTypes, Utils } from "@/utils/Utils";
import AsyncStorage from "@/platform/async-storage";
import { marcarSaidaDoApp, router } from "@/navigation";
import { irParaLoginDoTirvuAdm, lembrarSaidaParaLogin } from "@/platform/saida-do-app";
import { useEffect, useMemo, useState } from "react";
import { T, TC } from "@/utils/i18n";

export const PRIMARY = "#294956";

/**
 * Botão "Acessar Administrativo" (menu lateral e sheet do Menu). Oculto por enquanto:
 * para voltar a mostrar a quem tem `tem_acesso_adm`, é só trocar para `true`.
 */
export const ACESSO_ADMINISTRATIVO_VISIVEL: boolean = false;

/**
 * Item "Mudar Perfil de Acesso" (grupo Conta do menu lateral e do sheet do Menu). Oculto
 * por enquanto: para voltar a mostrar, é só trocar para `true`.
 */
export const MUDAR_PERFIL_VISIVEL: boolean = false;

/**
 * Cor de cada grupo do menu — usada SÓ na pastilha do ícone.
 *
 * Não é enfeite: é o que deixa a pessoa reencontrar o bloco pelo canto do olho antes
 * de ler o rótulo, num menu que ela abre dezenas de vezes por dia. Todas saem da
 * mesma faixa de luminosidade e de saturação baixa, derivadas do slate da marca —
 * cinco cores berrantes num menu utilitário viram ruído, não hierarquia.
 *
 * O "ponto" fica com o próprio PRIMARY porque é a função central do app: o bloco que
 * a pessoa procura primeiro é o que veste a cor da casa.
 */
export const COR_GRUPO = {
  ponto: PRIMARY,
  documentos: "#1c7a8c",
  aprendizado: "#5b57c4",
  operacoes: "#b06423",
  suprimentos: "#1a7f45",
  conta: "#5c7280",
} as const;

export type ItemMenu = {
  label: string;
  icon: string;
  route: string;
  sub?: string;
};

export type GrupoMenu = {
  id: string;
  titulo: string;
  cor: string;
  itens: ItemMenu[];
};

/** Itens da barra inferior (as "abas" visíveis). */
export type ItemBarra = {
  key: "home" | "arquivos" | "suporte" | "menu";
  label: string;
  icon: "home" | "folder" | "headset-mic" | "menu";
  badge?: boolean;
};

/**
 * Grupo "Conta" — fecha o menu, depois dos assuntos: é para onde se vai quando o que
 * se queria não era uma funcionalidade. "Mudar Perfil de Acesso" não depende de o
 * colaborador ter acesso ao painel administrativo — é a porta para os quatro ambientes —,
 * mas está oculto por enquanto (MUDAR_PERFIL_VISIVEL).
 *
 * WEB: no nativo estes três itens estão escritos à mão no JSX da BottomBar; aqui viram
 * dados para o MenuLateral desenhar os mesmos destinos. `acao: "mudarPerfil"` não é
 * rota de aba: é o `router.push("/environment-select")` (ver `mudarPerfil`).
 */
type ItemConta = {
  label: string;
  icon: string;
  route?: string;
  acao?: "mudarPerfil";
};

export const ITENS_CONTA: ItemConta[] = (
  [
    // TC: constante do arquivo (carrega antes de o idioma ser escolhido); a tela mostra TM(label).
    { label: TC("Meus Dados"), icon: "account-circle", route: "menu" },
    { label: TC("Biometria"), icon: "face", route: "biometria" },
    { label: TC("Mudar Perfil de Acesso"), icon: "swap-horiz", acao: "mudarPerfil" },
  ] satisfies ItemConta[]
).filter((it: ItemConta) => MUDAR_PERFIL_VISIVEL || it.acao !== "mudarPerfil");

/** Contagem de pendências de sincronização (mesmo formato de `contarPendenciasSync`). */
export type PendenciasSync = {
  pontos: number;
  assinaturas: number;
  rondas: number;
  livros: number;
  outras: number;
  total: number;
  // Não deu para conferir tudo o que está guardado no aparelho (leitura falhou).
  indeterminado: boolean;
};

export const PENDENCIAS_VAZIAS: PendenciasSync = {
  pontos: 0,
  assinaturas: 0,
  rondas: 0,
  livros: 0,
  outras: 0,
  total: 0,
  indeterminado: false,
};

const PENDENCIAS_INDETERMINADAS: PendenciasSync = {
  pontos: 0,
  assinaturas: 0,
  rondas: 0,
  livros: 0,
  outras: 0,
  total: 0,
  indeterminado: true,
};

/**
 * Regras de visibilidade do menu (permissões do posto, contrato, turno, conta inativa)
 * + badge de documentos pendentes. É o miolo da BottomBar do nativo, sem o desenho.
 */
export function useRegrasMenu() {
  const { userData, checklists, ensureChecklistsLoaded } = useAuth();

  // Badge de "documento pendente de assinatura" da tab Arquivos — CENTRALIZADO na
  // fonte única (documentoEstado): o campo cru `assinado` OSCILA ('S'/'N') e ignorava
  // contestação, marca local 'O' e o _Ass.pdf → o ponto vermelho piscava/mentia.
  // Snapshot recarregado quando o userData muda (mesmo gatilho do restante da UI).
  const [docSnapshotBar, setDocSnapshotBar] = useState<DocSnapshot | null>(
    null,
  );
  useEffect(() => {
    let ativo = true;
    carregarSnapshotLocalDocs()
      .then((s) => {
        if (ativo) setDocSnapshotBar(s);
      })
      .catch(() => {});
    return () => {
      ativo = false;
    };
  }, [userData]);

  const hasPendingDocuments = useMemo(() => {
    if (!userData?.documentos || !docSnapshotBar) return false;
    // sessaoDocsConfirmada(): MESMO gate anti-flicker da Home — no cold-open com cache
    // defasado, badge e modal decidem IGUAL (sem "pendente" na tab e "assinado" na Home).
    return resolverLista(
      userData,
      docSnapshotBar,
      Date.now(),
      sessaoDocsConfirmada(),
    ).some((e) => e.obrigatorioAssinar);
  }, [userData, docSnapshotBar]);

  // Conta inativa: restringe o acesso (sem ponto/câmera, menu reduzido, sem administrativo).
  const inativo = useMemo(
    () =>
      String(
        (userData as any)?.ativo_inativo ??
          (userData as any)?.done?.ativo_inativo ??
          "",
      )
        .trim()
        .toUpperCase() === "I",
    [userData],
  );

  // Acesso administrativo: liberado quando o colaborador também é usuário do painel
  // (tem_acesso_adm vindo do start). Abre o portal admin com auto-login (PortalWeb).
  // Oculto por enquanto (ACESSO_ADMINISTRATIVO_VISIVEL).
  const temAcessoAdm = useMemo(
    () =>
      ACESSO_ADMINISTRATIVO_VISIVEL &&
      Boolean(
        (userData as any)?.tem_acesso_adm ??
        (userData as any)?.done?.tem_acesso_adm,
      ),
    [userData],
  );
  const portalHash = useMemo(
    () =>
      String(
        (userData as any)?.hash ?? (userData as any)?.done?.hash ?? "",
      ).trim(),
    [userData],
  );

  // Acesso a Ronda / Livro de Ocorrência (seções habilitadas no start).
  const temAcessoRonda = useMemo(
    () =>
      Boolean(
        (userData as any)?.tem_acesso_ronda ??
        (userData as any)?.done?.tem_acesso_ronda,
      ),
    [userData],
  );
  const temAcessoLivro = useMemo(
    () =>
      Boolean(
        (userData as any)?.tem_acesso_livro ??
        (userData as any)?.done?.tem_acesso_livro,
      ),
    [userData],
  );
  const temAcessoOs = useMemo(
    () =>
      Boolean(
        (userData as any)?.tem_acesso_os ??
        (userData as any)?.done?.tem_acesso_os,
      ),
    [userData],
  );
  // Espelho de Ponto no MENU: liberado no posto E o colaborador registra ponto em algum
  // canal (ver utils/permissoes — a regra da Home é mais restritiva, e o porquê está lá).
  const permiteEspelho = useMemo(
    () => mostraEspelhoNoMenu(userData),
    [userData],
  );
  // Registrar ponto pelo app (permitePonto do /iniciar). Abre por padrão — só o 'N'
  // explícito bloqueia (ver utils/permissoes).
  const permitePonto = useMemo(
    () => permiteRegistrarPonto(userData),
    [userData],
  );
  // Registra ponto em QUALQUER canal (registra_ponto cru). Quem não registra não tem
  // histórico de batidas nem justificativa para consultar.
  const registraPontoOk = useMemo(() => registraPonto(userData), [userData]);
  // Livro de Ocorrência / OS: a empresa pode BLOQUEAR (permite_* === 'N'). Bloqueia só no
  // 'N' explícito — campo ausente/vazio não bloqueia (não regride antes do redeploy).
  const permiteLivro = useMemo(
    () =>
      String(
        (userData as any)?.permite_livro_ocorrencia ??
          (userData as any)?.done?.permite_livro_ocorrencia ??
          "",
      )
        .trim()
        .toUpperCase() !== "N",
    [userData],
  );
  // permite_livro_sem_registro === 'S': o Livro de Ocorrência pode ser acessado MESMO
  // sem o colaborador ter batido o ponto (dispensa o "em turno"). 'S' explícito habilita.
  const permiteLivroSemRegistro = useMemo(
    () =>
      String(
        (userData as any)?.permite_livro_sem_registro ??
          (userData as any)?.done?.permite_livro_sem_registro ??
          "",
      )
        .trim()
        .toUpperCase() === "S",
    [userData],
  );
  const permiteOs = useMemo(
    () =>
      String(
        (userData as any)?.permite_os ??
          (userData as any)?.done?.permite_os ??
          "",
      )
        .trim()
        .toUpperCase() !== "N",
    [userData],
  );
  // Interruptores do POSTO, um por tela (16/09/2026): `permite_enviar_atestado` e
  // `permite_solicitar_abono` de tab_adm_clientes, via /iniciar (persistido — vale
  // offline). Até aqui o atestado herdava o do abono. Só o 'N' explícito esconde.
  const permiteAtestado = useMemo(
    () => permiteEnviarAtestado(userData),
    [userData],
  );
  const permiteAbono = useMemo(
    () => permiteSolicitarAbono(userData),
    [userData],
  );
  // Escala 12x36/24x72 e o interruptor "Troca de Plantão" do posto (permite_troca_plantao).
  const trocaPlantaoOk = useMemo(
    () => podeUsarTrocaPlantao(userData),
    [userData],
  );
  // permite_produtos: se 'N', oculta do menu o "Estoque e Pedidos".
  // Bloqueia só no 'N' explícito (ausente/vazio não esconde).
  const permiteProdutos = useMemo(
    () =>
      String(
        (userData as any)?.permite_produtos ??
          (userData as any)?.done?.permite_produtos ??
          "",
      )
        .trim()
        .toUpperCase() !== "N",
    [userData],
  );
  // Treinamentos só aparece quando a empresa habilita (permite_treinamentos === 'S').
  const permiteTreinamentos = useMemo(
    () =>
      String(
        (userData as any)?.permite_treinamentos ??
          (userData as any)?.done?.permite_treinamentos ??
          "",
      )
        .trim()
        .toUpperCase() === "S",
    [userData],
  );
  // Livro de Ocorrências e Ronda aparecem no menu apenas quando o colaborador está
  // EM TURNO (ponto ÍMPAR hoje). Sem ter batido o ponto (par/zero) o item não aparece —
  // mesma regra que as telas usam internamente (pontoAberto). Como o Registro dá refresh
  // do userData ao bater o ponto, o menu destrava NA HORA, sem pull-to-refresh.
  //
  // Ronda NÃO é mais gateada por `permite_ronda`: esse flag é calculado no servidor a
  // partir do último ponto e vem CACHEADO (15s) no /iniciar — logo após bater ele ainda
  // volta 'N' e escondia a Ronda. O app não tem como saber "meu posto tem ronda" de forma
  // FRESCA e client-side (o ponto recém-batido nem sempre carrega id_adm_cliente após a
  // confirmação do servidor, e a config de ronda depende do ponto já estar no banco — escrita
  // assíncrona). Então liberamos com acesso + em turno; a própria tela de Ronda trata o caso
  // "sem ronda no posto" (lista vazia). Precisão exata exigiria mudança no t-credentials.
  const emTurno = useMemo(() => pontoAbertoHoje(userData), [userData]);

  // "Checklists" só entra no menu quando o colaborador tem ao menos UM checklist de
  // gatilho LIVRE liberado para ele — checklist de gatilho de ponto não aparece aqui,
  // porque ele é disparado pela batida e não faz sentido executar avulso.
  const temChecklistLivre = useMemo(
    () => checklistsLivres(checklists).length > 0,
    [checklists],
  );

  // Os templates chegam pelo refreshStaticData do login, mas a hidratação pode devolver
  // lista vazia — sem isto o item do menu não apareceria até o próximo start.
  useEffect(() => {
    void ensureChecklistsLoaded();
  }, [ensureChecklistsLoaded]);

  /**
   * Menu agrupado por ASSUNTO.
   *
   * Antes era uma lista chapada de até dez itens separados por um traço: tudo com o
   * mesmo peso visual, na ordem em que as funcionalidades foram nascendo — os
   * "Arquivos Recebidos" caíam no meio do bloco de ponto, entre o Espelho e as Ordens
   * de Serviço. Quem abre o menu procura por CATEGORIA ("onde é a ronda?"), e uma
   * lista sem hierarquia obriga a ler tudo, toda vez.
   *
   * ⚠️ Grupo sem NENHUM item visível não é renderizado (o `.filter` no fim). Sem isso,
   * quem não tem o módulo veria o cabeçalho "Operações" com um card vazio embaixo —
   * pior do que não mostrar nada, porque parece defeito.
   *
   * ⚠️ As condições de visibilidade são as MESMAS de antes, item por item, incluindo o
   * caso `inativo`. Isto reorganiza a APRESENTAÇÃO; quem pode ver o quê não mudou.
   */
  const menuGrupos = useMemo(() => {
    const grupos: GrupoMenu[] = inativo
      ? [
          // Conta inativa: só os arquivos recebidos (a conta em si vive no rodapé,
          // fora dos grupos). Mesma regra de antes, agora sob o rótulo certo.
          {
            id: "documentos",
            titulo: T("Documentos"),
            cor: COR_GRUPO.documentos,
            itens: [
              {
                label: T("Arquivos Recebidos"),
                icon: "folder",
                route: "arquivos",
              },
            ],
          },
        ]
      : [
          {
            id: "ponto",
            titulo: T("Ponto Eletrônico"),
            cor: COR_GRUPO.ponto,
            itens: [
              // Sem permissão de ponto o item nem entra na lista: levar a uma tela
              // que só sabe recusar a batida é pior do que não oferecer o caminho.
              ...(permitePonto
                ? [
                    {
                      label: T("Ponto Eletrônico"),
                      icon: "photo-camera",
                      route: "camera",
                    },
                  ]
                : []),
              // Histórico e justificativas de PONTO: sem registro de ponto não há
              // batida para consultar nem justificar.
              ...(registraPontoOk
                ? [
                    {
                      label: T("Histórico de Registros"),
                      icon: "history",
                      route: "justificativas",
                    },
                  ]
                : []),
              ...(permiteEspelho
                ? [
                    {
                      label: T("Espelho de Ponto"),
                      icon: "assignment",
                      route: "espelho",
                    },
                  ]
                : []),
              // Atestado e abono, cada um com o seu interruptor do posto (16/09/2026).
              ...(permiteAtestado
                ? [
                    {
                      label: T("Enviar Atestado Médico"),
                      icon: "medical-services",
                      route: "atestado",
                    },
                  ]
                : []),
              // É o único item com subtítulo — o nome sozinho não diz o que cabe aqui.
              ...(permiteAbono
                ? [
                    {
                      label: T("Enviar Solicitação de Abono"),
                      sub: T("Compensação de horas, licença maternidade, gala, etc."),
                      icon: "event-available",
                      route: "abono",
                    },
                  ]
                : []),
              // Só escala 12x36/24x72 (30/10/2026): pedir, responder, acompanhar e os recibos.
              ...(trocaPlantaoOk
                ? [
                    {
                      label: T("Troca de Plantão"),
                      icon: "swap-horiz",
                      route: "trocaPlantao",
                    },
                  ]
                : []),
            ],
          },
          {
            id: "documentos",
            titulo: T("Documentos"),
            cor: COR_GRUPO.documentos,
            itens: [
              {
                label: T("Arquivos Recebidos"),
                icon: "folder",
                route: "arquivos",
              },
            ],
          },
          {
            id: "aprendizado",
            titulo: T("Aprendizado"),
            cor: COR_GRUPO.aprendizado,
            itens: [
              ...(permiteTreinamentos
                ? [
                    {
                      label: T("Treinamentos"),
                      icon: "school",
                      route: "treinamentos",
                    },
                  ]
                : []),
            ],
          },
          {
            id: "operacoes",
            titulo: T("Operações"),
            cor: COR_GRUPO.operacoes,
            itens: [
              ...(temChecklistLivre
                ? [
                    {
                      label: T("Checklists"),
                      icon: "fact-check",
                      route: "checklists",
                    },
                  ]
                : []),
              ...(temAcessoLivro &&
              permiteLivro &&
              (emTurno || permiteLivroSemRegistro)
                ? [
                    {
                      label: T("Livro de Ocorrências"),
                      icon: "book",
                      route: "livroOcorrencia",
                    },
                  ]
                : []),
              ...(temAcessoOs && permiteOs
                ? [
                    {
                      label: T("Ordens de Serviço"),
                      icon: "assignment-late",
                      route: "ordemServico",
                    },
                  ]
                : []),
              ...(temAcessoRonda && emTurno
                ? [{ label: T("Ronda"), icon: "location-on", route: "ronda" }]
                : []),
            ],
          },
          {
            id: "suprimentos",
            titulo: T("Suprimentos"),
            cor: COR_GRUPO.suprimentos,
            itens: [
              ...(permiteProdutos
                ? [
                    {
                      label: T("Estoque e Pedidos"),
                      icon: "inventory",
                      route: "estoque",
                    },
                  ]
                : []),
            ],
          },
        ];

    return grupos.filter((g) => g.itens.length > 0);
  }, [
    temAcessoRonda,
    temAcessoLivro,
    temAcessoOs,
    permiteEspelho,
    permitePonto,
    registraPontoOk,
    permiteLivro,
    permiteLivroSemRegistro,
    permiteOs,
    permiteProdutos,
    permiteTreinamentos,
    permiteAtestado,
    permiteAbono,
    trocaPlantaoOk,
    temChecklistLivre,
    emTurno,
    inativo,
  ]);

  const items: ItemBarra[] = [
    { key: "home", label: T("Home"), icon: "home" as const },
    {
      key: "arquivos",
      label: T("Arquivos"),
      icon: "folder" as const,
      badge: hasPendingDocuments,
    },
    { key: "suporte", label: T("Suporte"), icon: "headset-mic" as const },
    { key: "menu", label: T("Menu"), icon: "menu" as const },
  ];

  return {
    userData,
    items,
    menuGrupos,
    hasPendingDocuments,
    inativo,
    temAcessoAdm,
    portalHash,
    permitePonto,
  };
}

/** Caminho web de uma rota de aba (`/(tabs)/x` → `/x` na normalização do shim). */
export const caminhoDaAba = (route: string) => `/${route}`;

/**
 * `navigation.navigate(name)` do Tab Navigator: ir para a aba já ativa não faz nada.
 * WEB: `router.push("/<aba>")`, sem empilhar a mesma rota de novo.
 */
export function navegarParaAba(route: string, rotaAtual?: string | null) {
  const caminho = caminhoDaAba(route);
  if (rotaAtual === caminho) return;
  router.push(caminho as any);
}

/** Aba Suporte: antes abria o bottomsheet de suporte técnico (desativado); agora abre a
 * página do chat de suporte. */
export function abrirSuporteChat() {
  router.push("/suporte-chat" as any);
}

/**
 * Botão central (câmera) — registrar ponto.
 *
 * Desativado (e não escondido): o botão central é a âncora visual da barra — tirá-lo
 * deixaria um buraco no meio da navegação. Cinza + motivo explica.
 */
export function registrarPonto(
  inativo: boolean,
  permitePonto: boolean,
  rotaAtual?: string | null,
) {
  if (inativo) {
    Utils.Alert(
      AlertTypes.warning,
      T("Sua conta está inativa. Você não pode registrar ponto."),
    );
    return;
  }
  if (!permitePonto) {
    Utils.Alert(
      AlertTypes.warning,
      T("Seu cadastro não permite registrar ponto pelo aplicativo."),
    );
    return;
  }
  navegarParaAba("camera", rotaAtual);
}

/**
 * Acessar Administrativo — mesmo fluxo comprovado do EnvironmentSelect: persiste
 * ambiente+hash e usa replace (o PortalWeb lê do storage, de forma confiável).
 * fromApp=1 mostra o botão de voltar, que restaura o ambiente 'colaborador' ao sair
 * (sem sequestrar o startup).
 */
export async function acessarAdministrativo(portalHash: string) {
  await savePortalChoice("administrador", portalHash);
  router.replace({ pathname: "/portal-web", params: { fromApp: "1" } } as any);
}

/**
 * MUDAR PERFIL → tela de escolha de acesso (Colaborador · Administrador ·
 * Cliente · Prestador), que é a `EnvironmentSelect`.
 *
 * ⚠️ NÃO desloga: trocar de perfil não é sair. A sessão do colaborador continua
 * viva (inclusive a fila offline dele) e o usuário volta para ela sem relogar.
 */
export function mudarPerfil() {
  router.push("/environment-select" as any);
}

/**
 * Pendências que IMPEDEM sair: há algo a enviar, ou não deu para conferir.
 *
 * ⚠️ O "não deu para conferir" (`indeterminado`) BLOQUEIA desde 06/10/2026. Antes ele
 * liberava a saída "para não travar o usuário" — e o clear() logo depois apagava o que não
 * tinha sido lido. Era exatamente o caso de quem atualizava o app pela loja: a fila grande
 * demais para ler no Android dava `indeterminado`, a pessoa saía e entrava "para resolver"
 * e as batidas offline sumiam.
 */
export async function pendenciasQueBloqueiam(): Promise<PendenciasSync | null> {
  const pend = await SyncService.contarPendenciasSync().catch(() => null);
  if (!pend) {
    return { ...PENDENCIAS_INDETERMINADAS };
  }
  return pend.total > 0 || pend.indeterminado ? (pend as PendenciasSync) : null;
}

/**
 * Sair de verdade (depois do "Sair" do modal de confirmação).
 *
 * `bloquear(pend)` é chamado quando a saída NÃO pode acontecer (com o que falta);
 * caso contrário limpa o storage preservando o que precisa sobreviver, faz signOut e
 * volta para o login do TirvuAdm (WEB; no app nativo, a escolha de ambiente).
 */
export async function executarSaida(
  signOut: () => Promise<unknown> | unknown,
  bloquear: (pend: PendenciasSync) => void,
) {
  // Confere DE NOVO: com o modal aberto, uma batida pode ter entrado na fila (o dreno e
  // as telas enfileiram em segundo plano).
  const bloqueio = await pendenciasQueBloqueiam();
  if (bloqueio) {
    bloquear(bloqueio);
    return;
  }
  // A fila em arquivo (services/FilaOffline.ts) não some com o clear(): ela é regravada
  // aqui só com o que precisa sobreviver (retidas de OUTRO tenant). Não deu, ou entrou
  // ação nova da sessão desde a conferência → não sai. Idem para as chaves que guardam
  // dado ainda não enviado fora da fila (complementos de checklist, diárias do prestador):
  // sem conseguir lê-las para regravar depois do clear(), não sai.
  let sobrevivem: [string, string][];
  try {
    await SyncService.prepararFilaParaSair();
    sobrevivem = await SyncService.lerChavesQueSobrevivemAoSair();
  } catch (e) {
    console.warn("[BottomBar] Sair bloqueado:", e);
    bloquear((await pendenciasQueBloqueiam()) ?? { ...PENDENCIAS_INDETERMINADAS });
    return;
  }
  // 🔒 Retidas de OUTRO tenant (guarda cross-tenant) sobrevivem ao wipe: o gate
  // de pendências só conta o tenant da sessão, então batidas/assinaturas de
  // outra empresa ainda podem estar na fila — o clear() as destruiria.
  const retidas = await SyncService.snapshotRetidasOutroTenant().catch(
    () => null,
  );
  await AsyncStorage.clear();
  if (sobrevivem.length > 0) {
    await AsyncStorage.multiSet(sobrevivem).catch((e: unknown) =>
      console.error(
        "[BottomBar] Dados guardados fora da fila NÃO voltaram depois do clear():",
        e,
      ),
    );
  }
  if (retidas) await SyncService.restaurarRetidasOutroTenant(retidas);
  // WEB: o colaborador-web é aberto pelo login do TirvuAdm ("Acesso do Colaborador"); ao sair,
  // volta para ele, já na opção Colaborador. A saída é marcada ANTES do signOut: sem sessão, as
  // guardas de rota mandariam para "/" (→ /intro) e essa navegação interna cancelaria a ida
  // para o login, que leva mais que a carência delas para carregar.
  marcarSaidaDoApp();
  try {
    await signOut();
  } catch (e) {
    // signOut pode rejeitar numa das limpezas (deleteJSONFile relança). O storage já foi
    // limpo acima: mesmo meio-deslogado, o destino é o login — segue (igual ao logoff remoto).
    console.warn("[Sair] signOut falhou parcialmente:", e);
  }
  // Lembra a saída: o Voltar do navegador (ou o endereço aberto sem sessão) leva de novo ao
  // login do TirvuAdm, e não à intro. O próximo login apaga a marca.
  lembrarSaidaParaLogin();
  irParaLoginDoTirvuAdm();
}

/** Linhas do modal "Não é possível sair" (o que falta sincronizar). */
export function linhasPendencias(
  pendenciasSync: PendenciasSync,
): { icon: string; label: string }[] {
  return [
    pendenciasSync.pontos > 0
      ? {
          icon: "fingerprint",
          label: T("{pontos} registro{v} de ponto", { pontos: pendenciasSync.pontos, v: pendenciasSync.pontos > 1 ? "s" : "" }),
        }
      : null,
    pendenciasSync.assinaturas > 0
      ? {
          icon: "draw",
          label: T("{assinaturas} assinatura{v} de documento", { assinaturas: pendenciasSync.assinaturas, v: pendenciasSync.assinaturas > 1 ? "s" : "" }),
        }
      : null,
    pendenciasSync.rondas > 0
      ? {
          icon: "security",
          label: T("{rondas} ronda{v}", { rondas: pendenciasSync.rondas, v: pendenciasSync.rondas > 1 ? "s" : "" }),
        }
      : null,
    pendenciasSync.livros > 0
      ? {
          icon: "menu-book",
          label: T("{livros} livro{v} de ocorrência", { livros: pendenciasSync.livros, v: pendenciasSync.livros > 1 ? "s" : "" }),
        }
      : null,
    pendenciasSync.outras > 0
      ? {
          icon: "sync",
          label: T("{outras} outra{v} pendência{v} de sincronização", { outras: pendenciasSync.outras, v: pendenciasSync.outras > 1 ? "s" : "" }),
        }
      : null,
    pendenciasSync.indeterminado
      ? {
          icon: "help-outline",
          label:
            T("Registros guardados neste aparelho que não deu para conferir agora"),
        }
      : null,
  ].filter(Boolean) as { icon: string; label: string }[];
}

/**
 * WEB: a BottomBar (celular/tablet) e o MenuLateral (desktop ≥1024px) podem estar
 * montados ao mesmo tempo (o shell esconde um deles por CSS). Os alvos do tour
 * (`bottombar:*`) só são registrados pelo que está VISÍVEL, senão o tour mediria um
 * nó escondido.
 */
export const ehDesktop = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(min-width: 1024px)").matches;
